import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ROOMS } from "../../mock";
import { useMedia, resolveUrl } from "../../context/MediaContext";
import { KineticText, Reveal } from "../../lib/motion";

function RoomCard({ room, i, mobile, src }) {
  return (
    <div className={`relative shrink-0 overflow-hidden shadow-2xl group ${mobile ? "w-full h-[62vh] min-h-[420px] rounded-[24px]" : "w-[86vw] sm:w-[70vw] md:w-[46vw] h-[56vh] sm:h-[54vh] rounded-[24px] md:rounded-[32px]"}`}>
      <img src={src} alt={room.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#04140E]/90 via-[#04140E]/25 to-transparent" />
      <span className="absolute top-4 left-5 md:top-6 md:left-6 font-display font-extrabold text-white/25 text-6xl md:text-7xl">0{i + 1}</span>
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7">
        <h3 className="font-display font-bold text-white text-2xl md:text-3xl">{room.title}</h3>
        <span className="inline-block mt-2 rounded-full bg-[var(--terracotta)] text-white text-[11px] md:text-xs font-semibold px-3 py-1">{room.subtitle}</span>
        <p className="text-white/75 text-[13px] md:text-sm leading-relaxed mt-3 max-w-md">{room.text}</p>
      </div>
    </div>
  );
}

// Desktop: pinned horizontal scroll tour of the bedrooms (driven by vertical scroll).
// Mobile: simple vertical stack (no awkward sideways scrolling).
export default function RoomsGoldenHour() {
  const { slots } = useMedia();
  const ref = useRef(null);
  const trackRef = useRef(null);
  const [distance, setDistance] = useState(0);

  const { scrollYProgress } = useScroll({ target: ref });
  const x = useTransform(scrollYProgress, [0.08, 0.95], [0, -distance]);

  useEffect(() => {
    const measure = () => {
      if (!trackRef.current) return;
      const track = trackRef.current;
      const overflow = track.scrollWidth - window.innerWidth;
      setDistance(Math.max(0, overflow + 24));
    };
    measure();
    window.addEventListener("resize", measure);
    const t = setTimeout(measure, 400); // after images/layout settle
    return () => {
      window.removeEventListener("resize", measure);
      clearTimeout(t);
    };
  }, []);

  return (
    <>
      {/* Mobile: vertical stack */}
      <section className="md:hidden relative px-5 py-20">
        <div className="max-w-[1300px] mx-auto w-full">
          <span className="font-body text-[10px] tracking-[0.3em] uppercase text-white/70">Golden Hour &middot; 7:45 PM</span>
          <h2 className="font-display font-extrabold text-white leading-[0.9] text-5xl mt-2">
            <KineticText text="Room to" />
            <span className="font-serif-i italic font-normal text-[var(--amber-light)]"> unwind</span>
          </h2>
          <div className="flex flex-col gap-5 mt-8">
            {ROOMS.map((room, i) => (
              <Reveal key={room.id}>
                <RoomCard room={room} i={i} mobile src={resolveUrl(slots[room.imgKey])} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Desktop: pinned horizontal scroll */}
      <section ref={ref} className="hidden md:block relative h-[440vh]">
        <div className="sticky top-0 h-[100svh] overflow-hidden flex flex-col justify-center">
          <div className="px-6 pt-24 pb-6 max-w-[1300px] mx-auto w-full">
            <span className="font-body text-xs tracking-[0.3em] uppercase text-white/70">Golden Hour &middot; 7:45 PM</span>
            <h2 className="font-display font-extrabold text-white leading-[0.9] text-8xl mt-3">
              <KineticText text="Room to" />
              <span className="font-serif-i italic font-normal text-[var(--amber-light)]"> unwind</span>
            </h2>
          </div>

          <motion.div ref={trackRef} style={{ x }} className="flex gap-6 pl-[max(24px,calc((100vw-1300px)/2+24px))] pr-5">
            {ROOMS.map((room, i) => (
              <RoomCard key={room.id} room={room} i={i} src={resolveUrl(slots[room.imgKey])} />
            ))}
          </motion.div>
        </div>
      </section>
    </>
  );
}
